import { Dom } from "../../../common/dom";
import { Helper } from "../../../common/helper";
import { Icon, IconHelper } from "../../../common/icon-helper";
import { ListDropdown } from "../list-dropdown";
import { EditControl } from "../../form-row";

export class TimeControl implements EditControl
{
    private static readonly patternRegex = new RegExp("^((0|1|)[0-9]|2[0-3]):[0-5][0-9]$");

    private readonly input: HTMLInputElement;
    private readonly container: HTMLDivElement;
    private dropdown: ListDropdown;
    private onChangeAction = () =>
    {};

    constructor(timeAccuracy: number)
    {
        const hasAccuracy = timeAccuracy && timeAccuracy > 0;
        this.container = Dom.createDiv();
        this.container.style.position = "relative";

        const inputGroup = Dom.addDiv(this.container, "w-input-group");
        this.input = Dom.add("input", inputGroup, "w-form-control");
        this.input.style.paddingLeft = "8px";
        this.input.style.paddingRight = "8px";

        const addon = Dom.addSpan(inputGroup, "w-input-group-addon");
        Dom.addSpan(addon).className = IconHelper.getIconClass(Icon.Time);

        if (Helper.isMobile())
        {
            // use the native time input so the phone's own picker is applied
            this.input.type = "time";
            if (hasAccuracy)
            {
                // step is in seconds; a multiple of 60 keeps the seconds field hidden
                this.input.step = (timeAccuracy * 60).toString();
            }
            addon.addEventListener("click", () => this.openNativePicker());
        }
        else
        {
            // use the configured accuracy when it is 10 min or more; otherwise default to 15 min
            const suggestionStep = hasAccuracy && timeAccuracy >= 10 ? timeAccuracy : 15;

            this.input.type = "text";
            this.input.autocomplete = "off";
            this.input.placeholder = "--:--";

            this.dropdown = new ListDropdown(
                this.input,
                this.container,
                (value) =>
                {
                    this.input.value = value;
                    this.input.dispatchEvent(new Event("change"));
                },
                // focus, click, typing and ArrowDown all show the time list
                () => this.showMenu()
            );

            const items: string[] = [];
            for (let minutes = 0; minutes < 24 * 60; minutes += suggestionStep)
            {
                items.push(TimeControl.formatMinutes(minutes));
            }
            this.dropdown.setItems(items);

            addon.addEventListener("click", () =>
            {
                this.input.focus();
                this.showMenu();
            });
        }

        this.input.addEventListener("change", () =>
        {
            // round to the configured accuracy when set; otherwise keep what the user typed.
            // round on total minutes from midnight so it matches how the options are generated
            // (and the native input's step) even when the accuracy does not divide 60.
            if (hasAccuracy)
            {
                const value = this.getValue();
                if (value)
                {
                    const hourMinute = value.split(":");
                    const total = parseInt(hourMinute[0]) * 60 + parseInt(hourMinute[1]);
                    const rounded = Math.floor(total / timeAccuracy) * timeAccuracy;
                    this.input.value = TimeControl.formatMinutes(rounded);
                }
            }
            this.onChangeAction();
        });
    }

    private static formatMinutes(totalMinutes: number): string
    {
        const hour = Math.floor(totalMinutes / 60);
        const minute = totalMinutes % 60;
        return `${hour < 10 ? "0" : ""}${hour}:${minute < 10 ? "0" : ""}${minute}`;
    }

    private static parseToMinutes(value: string): number | null
    {
        // full time: H:MM / HH:MM
        const full = value.match(/^(\d{1,2}):(\d{2})$/);
        if (full)
        {
            const hour = parseInt(full[1]);
            const minute = parseInt(full[2]);
            return hour > 23 || minute > 59 ? null : hour * 60 + minute;
        }
        // partial: hours + first minute digit (e.g. "09:3") -> treat digit as the tens of the minutes
        const partialMinute = value.match(/^(\d{1,2}):(\d)$/);
        if (partialMinute)
        {
            const hour = parseInt(partialMinute[1]);
            const minute = parseInt(partialMinute[2]) * 10;
            return hour > 23 ? null : hour * 60 + minute;
        }
        // partial: only hours typed (e.g. "9", "09" or "09:") -> start of that hour
        const hoursOnly = value.match(/^(\d{1,2}):?$/);
        if (hoursOnly)
        {
            const hour = parseInt(hoursOnly[1]);
            return hour > 23 ? null : hour * 60;
        }
        return null;
    }

    private nearestIndex(): number
    {
        const minutes = TimeControl.parseToMinutes(this.input.value.trim());
        if (minutes === null)
        {
            return -1;
        }
        let best = -1;
        let bestDiff = Number.POSITIVE_INFINITY;
        this.dropdown.items.forEach((value, index) =>
        {
            const itemMinutes = TimeControl.parseToMinutes(value);
            if (itemMinutes === null)
            {
                return;
            }
            const diff = Math.abs(itemMinutes - minutes);
            if (diff < bestDiff)
            {
                bestDiff = diff;
                best = index;
            }
        });
        return best;
    }

    private showMenu()
    {
        this.dropdown.showMenu();
        // highlight the nearest option (scrolled to the top), or clear the highlight
        // when the input no longer parses — otherwise a stale option stays selectable
        const index = this.nearestIndex();
        this.dropdown.highlight(index, index >= 0);
    }

    private openNativePicker()
    {
        const input = this.input as HTMLInputElement & { showPicker?: () => void };
        if (typeof input.showPicker === "function")
        {
            input.showPicker();
        }
        else
        {
            this.input.focus();
        }
    }

    initialize(parent: HTMLElement)
    {
        parent.append(this.container);
    }

    async setValue(value: string)
    {
        let normalized = value ? value : "";
        if (normalized.length === 4)
        {
            // pad "9:00" -> "09:00" so the native time input accepts it
            normalized = "0" + normalized;
        }
        this.input.value = normalized;
    }

    getValue()
    {
        if (!this.validate())
        {
            return null;
        }
        const value = this.input.value;
        if (value === "" || value === null)
        {
            return null;
        }
        return value.length === 4 ? "0" + value : value;
    }

    onChange(action: () => void)
    {
        this.onChangeAction = action;
    }

    validate(): boolean
    {
        const value = this.input.value;
        return !value || TimeControl.patternRegex.test(value);
    }

    focus()
    {
        this.input.focus();
    }
}
